import {Popover} from '@mui/material'
import React, {PropsWithChildren, useEffect} from 'react'
import {ArrowEdge, computePopupArrow} from '../../utils/popupArrow.utils'

type PopupProps = {
  showTip?: boolean
  isShowPadding?: boolean
  backgroundColor?: string
  anchorPosition: {top: number; left: number} | null
  setAnchorPosition: React.Dispatch<
    React.SetStateAction<{top: number; left: number} | null>
  >
  isChangedLeft?: boolean
}

// Minimum distance the arrow keeps from a Paper corner, so it never sits
// exactly on the rounded border-radius.
const ARROW_INSET = 14

export default function Popup({
  showTip = true,
  isShowPadding = true,
  backgroundColor,
  anchorPosition,
  setAnchorPosition,
  children,
  isChangedLeft = false,
}: PropsWithChildren<PopupProps>) {
  const contentRef = React.useRef<HTMLDivElement>(null)
  const [contentSize, setContentSize] = React.useState({width: 0, height: 0})
  const [isLeft, setIsLeft] = React.useState(false)
  const [arrowEdge, setArrowEdge] = React.useState<ArrowEdge>('top')
  // Pixel offset of the arrow along the Paper's edge, measured from the
  // Paper's actual on-screen position (see the second effect below) — not
  // assumed from the anchor + planned offsets, which can diverge from reality
  // whenever the viewport forces MUI to clamp the Paper near a screen edge.
  const [arrowAlong, setArrowAlong] = React.useState<number | null>(null)

  // Pass 1: measure the popup's natural size and decide which edge
  // (top/bottom/left/right) fits, after MUI's Portal has finished laying out
  // the Paper. requestAnimationFrame ensures the Paper DOM element is fully
  // rendered before we read its dimensions — a plain useEffect runs too early.
  useEffect(() => {
    if (!anchorPosition) {
      setContentSize({width: 0, height: 0})
      setArrowEdge('top')
      setIsLeft(false)
      setArrowAlong(null)
      return
    }

    const rafId = requestAnimationFrame(() => {
      if (!contentRef.current) return

      const width = contentRef.current.offsetWidth
      // scrollHeight gives the natural height regardless of MUI's max-height clamping
      const height = contentRef.current.scrollHeight

      const nextIsLeft =
        window.innerWidth - anchorPosition.left < width + 30
          ? false
          : anchorPosition.left < width + 30 || isChangedLeft

      const result = computePopupArrow(
        {
          anchorPosition,
          contentSize: {width, height},
          windowWidth: window.innerWidth,
          windowHeight: window.innerHeight,
        },
        nextIsLeft,
      )

      setContentSize({width, height})
      setIsLeft(nextIsLeft)
      setArrowEdge(result.arrowEdge)
    })

    return () => cancelAnimationFrame(rafId)
  }, [anchorPosition, isChangedLeft])

  // Pass 2: once the edge choice from pass 1 causes MUI to (re)position the
  // Paper, read where it *actually* landed and pin the arrow to the point on
  // that box closest to the real anchor. This is what keeps the arrow correct
  // even when the Paper doesn't end up exactly where pass 1 planned — e.g. a
  // tall popup opened near the top/bottom of a short viewport, which MUI's
  // own boundary handling can still nudge.
  //
  // Deliberately reads `style.top`/`style.left` + offsetWidth/offsetHeight,
  // NOT getBoundingClientRect(): MUI's open transition (Grow) animates via a
  // CSS `scale()` transform, which getBoundingClientRect reports mid-flight
  // (a shrunken, still-animating box). The inline top/left MUI itself writes
  // via setPositioningStyles(), and the offset* layout dimensions, are both
  // unaffected by that transform and already final as soon as they're set —
  // no need to wait for the animation to finish.
  useEffect(() => {
    if (!anchorPosition || contentSize.width === 0) return

    const rafId = requestAnimationFrame(() => {
      const el = contentRef.current
      if (!el) return

      const paperTop = parseFloat(el.style.top || '0')
      const paperLeft = parseFloat(el.style.left || '0')
      const isHorizontal = arrowEdge === 'left' || arrowEdge === 'right'
      const raw = isHorizontal
        ? anchorPosition.top - paperTop
        : anchorPosition.left - paperLeft
      const axisSize = isHorizontal ? el.offsetHeight : el.offsetWidth

      setArrowAlong(
        Math.min(
          Math.max(raw, ARROW_INSET),
          Math.max(axisSize - ARROW_INSET, ARROW_INSET),
        ),
      )
    })

    return () => cancelAnimationFrame(rafId)
  }, [anchorPosition, arrowEdge, isLeft, contentSize])

  const result = anchorPosition
    ? computePopupArrow(
        {
          anchorPosition,
          contentSize,
          windowWidth: window.innerWidth,
          windowHeight: window.innerHeight,
        },
        isLeft,
      )
    : null

  const isHorizontalArrow = arrowEdge === 'left' || arrowEdge === 'right'

  return (
    <Popover
      open={Boolean(anchorPosition)}
      transformOrigin={{
        vertical: result?.transformOriginVertical ?? 'top',
        horizontal: result?.transformOriginHorizontal ?? 'right',
      }}
      anchorReference='anchorPosition'
      anchorPosition={
        anchorPosition && result
          ? {
              top: anchorPosition.top + result.anchorTopOffset,
              left: anchorPosition.left + result.anchorLeftOffset,
            }
          : undefined
      }
      onClose={() => {
        setAnchorPosition(null)
      }}
      // computePopupArrow already decides which edge (top/bottom/left/right)
      // safely fits the anchor + Paper size. MUI's own marginThreshold clamp
      // would otherwise shift the Paper independently near a screen edge,
      // decoupling it from the arrow (which stays pinned to the Paper's
      // corner) — disable it so our calculation is the only thing moving it.
      marginThreshold={0}
      disableRestoreFocus
      disableEnforceFocus
      slotProps={{
        paper: {
          ref: contentRef,
          elevation: 0,
          sx: {
            overflow: 'visible',
            borderRadius: '5px',
            boxShadow: '0px 0px 20px 2px rgba(0, 0, 0, 0.25)',
            padding: isShowPadding ? '10px' : 0,
            '&::before': {
              content: '""',
              display: showTip ? 'block' : 'none',
              position: 'absolute',
              width: 10,
              height: 10,
              borderBottom:
                arrowEdge === 'top' || arrowEdge === 'left'
                  ? 'none'
                  : undefined,
              borderRight:
                arrowEdge === 'top' || arrowEdge === 'right'
                  ? 'none'
                  : undefined,
              borderTop:
                arrowEdge === 'bottom' || arrowEdge === 'right'
                  ? 'none'
                  : undefined,
              borderLeft:
                arrowEdge === 'bottom' || arrowEdge === 'left'
                  ? 'none'
                  : undefined,
              bgcolor: backgroundColor ?? 'background.paper',
              zIndex: 0,
              // Position arrow on the correct edge
              ...(arrowEdge === 'top' && {
                top: 0,
                transform: 'translateX(-50%) translateY(-50%) rotate(45deg)',
              }),
              ...(arrowEdge === 'bottom' && {
                bottom: 0,
                transform: 'translateX(-50%) translateY(50%) rotate(45deg)',
              }),
              ...(arrowEdge === 'left' && {
                left: 0,
                transform: 'translateX(-50%) translateY(-50%) rotate(45deg)',
              }),
              ...(arrowEdge === 'right' && {
                right: 0,
                transform: 'translateX(50%) translateY(-50%) rotate(45deg)',
              }),
              // Align arrow with anchor along the edge axis, using the
              // measured position once available (see pass 2 above) so it
              // tracks the Paper's real on-screen location, falling back to a
              // reasonable guess for the first paint before it's measured.
              ...(isHorizontalArrow
                ? {top: arrowAlong ?? contentSize.height / 2}
                : {left: arrowAlong ?? (isLeft ? contentSize.width - 20 : 20)}),
            },
          },
        },
      }}>
      {children}
    </Popover>
  )
}
